import React, { useState, useEffect, FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCompany } from "../context/CompanyContext"; // Import useCompany hook
import Loader from "./ui/Loader";
import ApprovalPendingScreen from "./ApprovalPendingScreen"; // Keep if still used for specific cases, otherwise remove
import { CompanyRegistrationCheckProps } from "../types/components.d";
import { CompanyVerificationStatus } from "../types/api.d"; // Import CompanyVerificationStatus
import Button from './ui/Button';
import brandLogo from '../assets/dhoondh2-logo.svg'

const CompanyRegistrationCheck: FC<CompanyRegistrationCheckProps> = ({
  children,
}): React.ReactElement => {
  const { user, loading: authLoading } = useAuth();
  const { companyDetails, loadingState, errorState , fetchCompanyDetails} = useCompany(); // Use useCompany hook
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState<boolean>(true);
  const handleRetry = async () => {
    if (!user) {
        navigate("/login");
        return;
      }

      if (user.role !== "Company") {
        navigate("/unauthorized");
        return;
      }

      // If company details are not yet loaded, try to fetch them
      if (!loadingState.companyDetails && user.companyId) {
        await fetchCompanyDetails(user.companyId);
        return; // Re-run effect once details are fetched
      }
  }
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      console.log("Checking Comapny RegistrationStatus", {
        authLoading,
        user,
        loadingState,
        companyDetails,
        errorState,
      });
      if (authLoading) return; // Wait for auth to load

      if (!user) {
        navigate("/login");
        return;
      }

      if (user.role !== "Company") {
        navigate("/unauthorized");
        return;
      }

      // If company details are not yet loaded, try to fetch them
      if (loadingState.companyDetails && user.companyId) {
        // await fetchCompanyDetails(user.companyId);
        return; // Re-run effect once details are fetched
      }

      if (errorState.companyDetails) {
        console.log("Error fetching company details:");
        // navigate('/company/register'); // Redirect to registration on error
        return;
      }

      // Now we have companyDetails (or an error occurred)
      if (companyDetails) {
        const verificationStatus: CompanyVerificationStatus =
          companyDetails.verificationStatus;

        if (verificationStatus === "Incomplete") {
          navigate("/company/register"); // Example: redirect with status
          return;
        }
        if (verificationStatus === "Pending") {
          setIsChecking(false);
          // The dashboard will show a notification banner for pending status
          return;
        } else if (verificationStatus === "Rejected") {
          // Handle rejected status, e.g., redirect to a specific page or show a message
          // navigate("/company/register?status=rejected"); // Example: redirect with status
          return;
        } else if (verificationStatus === "Verified") {
          setIsChecking(false);
          return;
        }
      } else {
        navigate("/company/register");
        return;
      }
    };

    checkRegistrationStatus();
  }, [companyDetails?.verificationStatus, loadingState.companyDetails, navigate]);

  if (authLoading || (loadingState.companyDetails && !companyDetails)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader />
          <p className="mt-4 text-gray-600">Loading company Details...</p>
        </div>
      </div>
    );
  }
  if (errorState.companyDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-100/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-50/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Action Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 'opacity-100 translate-y-0'`}>
            <div className="mx-auto flex items-center justify-center mb-8">
              <img src={brandLogo} alt="Dhoondh" className="h-12 aspect-auto md:h-16" />
            </div>
            <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <svg className="h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            {/* <h1 className="text-6xl md:text-8xl font-bold text-red-500 mb-6">403</h1> */}
            {/* <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Access Denied</h2> */}
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Failed to fetch Company Details
            </p>
          </div>
        </div>
            <div className="space-y-4">

                <Button onClick={handleRetry} variant="outline" size="lg" className="w-full py-4 text-lg font-semibold">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Try Again
                </Button>

            
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Need immediate assistance?
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a 
                    href="mailto:operations@dhoondh.in" 
                    className="text-sm font-medium text-primary-teaGreen hover:text-primary-lightGreen transition-colors"
                  >
                    operations@dhoondh.in
                  </a>
                  <span className="hidden sm:inline text-gray-300">•</span>
                  <a 
                    href="tel:+919316846322" 
                    className="text-sm font-medium text-primary-teaGreen hover:text-primary-lightGreen transition-colors"
                  >
                    +91 93168 46322
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    );
  }

  // If company is pending or approved, allow access to children
  // The children components should handle displaying appropriate messages/banners
  return children as React.ReactElement;
};

export default CompanyRegistrationCheck;
